/**
 * PHARMAKON — state machines
 *
 * Ported from the design's config/workflow-states.config.js.
 * Consumed by lib/state-machines.js + WorkflowStateChip primitive.
 */

export const WORKFLOW_STATES = {
  achat: {
    initial: "BROUILLON",
    states: {
      BROUILLON: {
        label: "Brouillon",
        tone: "warning",
        next: ["VALIDE"],
        editable: true,
        deletable: true,
      },
      VALIDE: {
        label: "Validé",
        tone: "success",
        // Refonte M-10 (03_DATABASE §2.12): a facture no longer moves to AVOIR — an avoir is its own
        // document imputed on it. VALIDE is edited until fully réglé through the rectification (D-13).
        next: [],
        editable: false,
        deletable: false,
      },
      // Retired state, kept readable for historical factures (never reached any more).
      AVOIR: {
        label: "Avoir émis",
        tone: "info",
        next: [],
        editable: false,
        deletable: false,
      },
    },
  },
  vente: {
    initial: "EN_COURS",
    states: {
      EN_COURS: {
        label: "En cours",
        tone: "warning",
        next: ["VALIDEE", "ANNULEE"],
        editable: true,
        deletable: true,
      },
      VALIDEE: {
        label: "Validée",
        tone: "success",
        next: ["ANNULEE"],
        editable: false,
        deletable: false,
      },
      ANNULEE: {
        label: "Annulée",
        tone: "danger",
        next: [],
        editable: false,
        deletable: false,
      },
    },
  },
  efacture: {
    initial: "CREEE",
    states: {
      CREEE: { label: "Créée", tone: "neutral", next: ["ENVOYEE"] },
      ENVOYEE: {
        label: "Envoyée",
        tone: "info",
        next: ["IMPORTEE", "REJETEE"],
      },
      IMPORTEE: {
        label: "Importée",
        tone: "info",
        next: ["VALIDEE", "PARTIELLEMENT_ACCEPTEE", "REJETEE"],
      },
      VALIDEE: { label: "Validée", tone: "success", next: ["PAYEE"] },
      PARTIELLEMENT_ACCEPTEE: {
        label: "Partiellement acceptée",
        tone: "warning",
        next: ["PAYEE"],
      },
      REJETEE: { label: "Rejetée", tone: "danger", next: [] },
      PAYEE: { label: "Payée", tone: "success", next: [] },
      // ACH-12 — efactureStore.cancel produces ANNULEE; without this entry the
      // chip printed the raw enum token.
      ANNULEE: { label: "Annulée", tone: "neutral", next: [] },
    },
  },
  caisse: {
    // Mirrors StatutCaisse in schema.prisma: OUVERTE | CLOTUREE.
    // (Server validator at stock.validator.js refuses any other value.)
    initial: "OUVERTE",
    states: {
      OUVERTE: { label: "Ouverte", tone: "success", next: ["CLOTUREE"] },
      CLOTUREE: { label: "Clôturée", tone: "neutral", next: [] },
    },
  },
  compte: {
    initial: "EN_ATTENTE",
    states: {
      EN_ATTENTE: {
        label: "En attente",
        tone: "warning",
        next: ["VALIDE", "REVISION_REQUISE", "REJETEE"],
      },
      VALIDE: { label: "Validé", tone: "success", next: ["SUSPENDUE"] },
      SUSPENDUE: { label: "Suspendu", tone: "danger", next: ["VALIDE"] },
      REVISION_REQUISE: {
        label: "Révision requise",
        tone: "warning",
        next: ["EN_ATTENTE"],
      },
      REJETEE: { label: "Rejeté", tone: "danger", next: [] },
    },
  },
  /**
   * TIP (2026-08) — le LIEN CONFRÈRE (`StatutLienConfrere`, serveur).
   *
   * Le libellé dépend du POINT DE VUE : une invitation en attente se dit
   * « Invitation envoyée » chez celle qui invite et « À répondre » chez celle
   * qui est invitée. Cette bilatéralité vit ICI, dans la configuration
   * (`label_envoyee` / `label_recue`), et JAMAIS dans une condition de
   * composant — c'est le procédé que le plan impose pour les états de transfert,
   * appliqué dès le lien.
   *
   * `next` liste ce que la MACHINE autorise ; qui a le droit de déclencher
   * quoi reste une décision du serveur (l'invitante ne peut pas accepter à la
   * place de l'invitée). Le client s'en sert pour n'afficher que les gestes
   * possibles, jamais pour autoriser.
   */
  lien_confrere: {
    initial: "EN_ATTENTE",
    states: {
      EN_ATTENTE: {
        label: "En attente",
        label_envoyee: "Invitation envoyée",
        label_recue: "À répondre",
        tone: "warning",
        next: ["ACCEPTE", "REFUSE", "REVOQUE", "EXPIRE"],
      },
      ACCEPTE: {
        label: "Confrère actif",
        tone: "success",
        next: ["SUSPENDU", "REVOQUE"],
      },
      SUSPENDU: {
        label: "Suspendu",
        tone: "warning",
        next: ["ACCEPTE", "REVOQUE"],
      },
      REFUSE: {
        label: "Refusé",
        tone: "danger",
        next: ["EN_ATTENTE"], // le refus n'est pas terminal : on peut réinviter
      },
      EXPIRE: {
        label: "Expiré",
        tone: "neutral",
        next: ["EN_ATTENTE"],
      },
      REVOQUE: {
        label: "Rompu",
        tone: "neutral",
        next: ["EN_ATTENTE"],
      },
    },
  },
};
